import React, { useCallback, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import omit from 'lodash.omit'
import { MenuItem, Typography, Box } from '@mui/material'
import { AppButton, Modal, TextField, Select, NotificationType } from 'src/components/ui'
import { useNotification, useRegistrant, useRegistrantEditingModal } from 'src/hooks'
import { FormValues, useRegistrantEditing } from 'src/hooks/useRegistrantEditing'
import { Utils } from 'src/lib/utils'
import { Country } from 'src/interfaces/state'
import AppInputMask from '../ui/InputMask'
import Grid from '@mui/material/Grid2'
import { usePathname } from 'next/navigation'

const SavingRegistrantInformation: React.FC = () => {
  const { getRules } = useRegistrantEditing()
  const { isVisibleModal, directToSeller, openModalIfNeed, closeModal } = useRegistrantEditingModal()
  const { openNotification } = useNotification()
  const { registrant, update, saveAddress } = useRegistrant()
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      country: Country.USA,
      firstName: '',
      lastName: '',
      street: '',
      apartment: '',
      city: '',
      state: '',
      zipcode: '',
      phone: '',
    },
  })
  const isDirectToSeller = useCallback(() => directToSeller(registrant), [registrant])()
  const watchCountry = watch('country')
  const countries = [Country.USA, Country.BERMUDA]
  const usaStates = Utils.getStates(Country.USA)
  const bermudaStates = Utils.getStates(Country.BERMUDA)
  const pathname = usePathname()

  useEffect(() => {
    const authRoutes = ['/registrant/registration', '/registrant/sign-in', '/']

    if (authRoutes.includes(pathname)) {
      return
    }

    openModalIfNeed(registrant)
  }, [registrant, pathname])

  const renderStateItem = (labelProperty: string, valueProperty: string) => (state: any) => (
    <MenuItem key={state[valueProperty]} value={state[valueProperty]}>
      {state[labelProperty]}
    </MenuItem>
  )

  const saveEmail = handleSubmit(async (data: FormValues) => {
    const [emailError] = await update({ email: data.email })
    if (emailError) {
      openNotification(emailError, NotificationType.ERROR)
      return
    }
    if (isDirectToSeller) {
      const address = omit(data, ['email'])
      address.phone = Utils.formatPhoneNumber(address.phone)
      const [addressError] = await saveAddress(address)
      if (addressError) {
        openNotification(addressError, NotificationType.ERROR)
        return
      }
    }
    closeModal()
  })

  return (
    <Modal isOpen={isVisibleModal} title="Please Add Your Details">
      <Grid container sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <Grid>
          <Controller
            defaultValue={registrant?.user.email || ''}
            name="email"
            control={control}
            rules={{
              required: 'Email is required',
            }}
            render={({ field }) => (
              <TextField
                fullWidth
                label="Email address"
                {...field}
                error={Boolean(errors.email?.message)}
                helperText={errors.email?.message}
                variant="outlined"
                type="email"
              />
            )}
          />
        </Grid>
        {isDirectToSeller && (
          <Grid container direction="column" alignItems="stretch" spacing={2}>
            <Grid>
              <Box sx={{ mt: 3 }}>
                <Typography variant="h3">Shipping Address</Typography>
              </Box>
            </Grid>
            <Grid>
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: getRules(watchCountry).firstName,
                }}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label="First name"
                    {...field}
                    error={Boolean(errors.firstName?.message)}
                    helperText={errors.firstName?.message}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid>
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: getRules(watchCountry).lastName,
                }}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label="Last name"
                    {...field}
                    error={Boolean(errors.lastName?.message)}
                    helperText={errors.lastName?.message}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid>
              <Controller
                name="country"
                control={control}
                rules={{
                  required: 'Country is required',
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    fullWidth
                    label="Country"
                    list={countries}
                    error={Boolean(errors.country)}
                    helperText={getRules(watchCountry).state as string}
                  />
                )}
              />
            </Grid>
            <Grid>
              <Controller
                name="street"
                control={control}
                rules={{
                  required: getRules(watchCountry).street,
                }}
                render={({ field }) => (
                  <TextField
                    label="Street address"
                    {...field}
                    fullWidth
                    error={Boolean(errors.street?.message)}
                    helperText={errors.street?.message}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid>
              <Controller
                name="apartment"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label="Apt, suite, floor #"
                    {...field}
                    error={Boolean(errors.apartment?.message)}
                    helperText={errors.apartment?.message}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid>
              <Controller
                name="city"
                control={control}
                rules={{
                  required: getRules(watchCountry).city,
                }}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label="City"
                    {...field}
                    error={Boolean(errors.city?.message)}
                    helperText={errors.city?.message}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid container spacing={2}>
              <Grid size={{ xs: 'grow' }}>
                <Controller
                  name="state"
                  control={control}
                  rules={{
                    required: getRules(watchCountry).state,
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      fullWidth
                      list={watchCountry === Country.USA ? usaStates : bermudaStates}
                      labelProperty="name"
                      valueProperty="alphaCode"
                      iterator={renderStateItem}
                      label="State"
                      helperText={getRules(watchCountry).state as string}
                      error={Boolean(errors.state)}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Controller
                  name="zipcode"
                  control={control}
                  rules={{
                    required: getRules(watchCountry).zipcode,
                  }}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      label="ZIP-code"
                      {...field}
                      error={Boolean(errors.zipcode?.message)}
                      helperText={errors.zipcode?.message}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid>
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: getRules(watchCountry).phone,
                }}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label="Mobile"
                    {...field}
                    error={Boolean(errors.phone?.message)}
                    helperText={errors.phone?.message}
                    variant="outlined"
                    slotProps={{
                      input: {
                        inputComponent: AppInputMask as any,
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
        <Grid>
          <AppButton fullWidth sx={{ mt: 3 }} size="large" variant="contained" onClick={saveEmail}>
            Save
          </AppButton>
        </Grid>
      </Grid>
    </Modal>
  )
}

export default SavingRegistrantInformation
